<template>
    <div>
        <v-card class="ma-3">
            <v-card-title>Select sensor & date</v-card-title>
            <v-card-text v-if="activeProbe">
                <v-select label="Sensor" v-model="selectedSensorId" :items="activeProbe.sensors" item-text="alias"
                          item-value="id"/>
                <v-menu
                        v-model="dateMenuVisible"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                                label="Date"
                                :value="date"
                                prepend-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                        ></v-text-field>
                    </template>
                    <v-date-picker
                            v-model="date"
                            first-day-of-week="1"
                            @cha="dateMenuVisible = false"
                    ></v-date-picker>
                </v-menu>
            </v-card-text>
            <v-card-actions>
                <v-btn :disabled="!selectedSensorId || !date" color="primary" @click="handleFetchClick">Fetch
                    measurements
                </v-btn>
            </v-card-actions>
        </v-card>
        <v-card class="ma-3" v-if="probeSensorMeasurements.length > 0">
            <v-card-title>
                Measurements
                <v-spacer/>
                <v-btn small @click="handleClearClick" :disabled="Object.keys(selectedSensorMeasurements).length === 0 && deleteMeasurements.length === 0"
                       color="primary">Clear selected measurements
                </v-btn>
            </v-card-title>
            <v-card-text>
                <v-simple-table>
                    <template v-slot:default>
                        <thead>
                        <tr>
                            <th class="text-left">Time</th>
                            <th class="text-right cursor-pointer" @click="handleHeaderClick('vol_moisture')">Vol moisture</th>
                            <th class="text-right cursor-pointer" @click="handleHeaderClick('er')">Er</th>
                            <th class="text-right cursor-pointer" @click="handleHeaderClick('salinity')">Soil salinity</th>
                            <th class="text-right cursor-pointer" @click="handleHeaderClick('j')">J</th>
                            <th class="text-right cursor-pointer" @click="handleHeaderClick('t')">Soil temp</th>
                            <th class="text-right cursor-pointer" @click="handleHeaderClick('i2c_temp')">Air temp</th>
                            <th class="text-right"></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr
                                v-for="measurement in probeSensorMeasurements"
                                :key="`probe_sensor_measurement_${measurement.id}`"
                                :style="{'background-color': deleteMeasurements.includes(measurement.id) ? '#FFCCCB' : 'white'}"
                        >
                            <td>{{ measurement.thedate | formatDateTime }}</td>
                            <td :style="{color: selectedSensorMeasurements && selectedSensorMeasurements[measurement.id] && selectedSensorMeasurements[measurement.id].includes('vol_moisture') ? 'red' : 'black'}"
                                @click="!isNaN(measurement.vol_moisture) ? handleMeasurementClick(measurement.id, 'vol_moisture') : null"
                                class="text-right cursor-pointer">{{ measurement.vol_moisture }}
                            </td>
                            <td :style="{color: selectedSensorMeasurements[measurement.id] && selectedSensorMeasurements[measurement.id].includes('er') ? 'red' : 'black'}"
                                @click="!isNaN(measurement.er) ? handleMeasurementClick(measurement.id, 'er') : null"
                                class="text-right cursor-pointer">{{ measurement.er }}
                            </td>
                            <td :style="{color: selectedSensorMeasurements[measurement.id] && selectedSensorMeasurements[measurement.id].includes('salinity') ? 'red' : 'black'}"
                                @click="!isNaN(measurement.salinity) ? handleMeasurementClick(measurement.id, 'salinity') : null"
                                class="text-right cursor-pointer">{{ measurement.salinity }}
                            </td>
                            <td :style="{color: selectedSensorMeasurements[measurement.id] && selectedSensorMeasurements[measurement.id].includes('j') ? 'red' : 'black'}"
                                @click="!isNaN(measurement.j) ? handleMeasurementClick(measurement.id, 'j') : null"
                                class="text-right cursor-pointer">{{ measurement.j }}
                            </td>
                            <td :style="{color: selectedSensorMeasurements[measurement.id] && selectedSensorMeasurements[measurement.id].includes('t') ? 'red' : 'black'}"
                                @click="!isNaN(measurement.t) ? handleMeasurementClick(measurement.id, 't') : null"
                                class="text-right cursor-pointer">{{ measurement.t }}
                            </td>
                            <td :style="{color: selectedSensorMeasurements[measurement.id] && selectedSensorMeasurements[measurement.id].includes('i2c_temp') ? 'red' : 'black'}"
                                @click="!isNaN(measurement.i2c_temp) ? handleMeasurementClick(measurement.id, 'i2c_temp') : null"
                                class="text-right cursor-pointer">{{ measurement.i2c_temp }}
                            </td>
                            <td class="text-right">
                                <v-icon
                                    title="Delete row"
                                    @click="handleDeleteClick(measurement.id)"
                                >
                                    {{ !deleteMeasurements.includes(measurement.id) ? 'mdi-trash-can-outline' : 'mdi-undo'}}
                                </v-icon>
                            </td>
                        </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import {mapActions, mapMutations, mapState} from "vuex";

export default {
    name: "StationMeasurement",
    data() {
        return {
            dateMenuVisible: false,
            selectedSensorId: null,
            date: null
        }
    },
    mounted() {
        this.fetchProbe(this.$route.params.id)
    },
    computed: {
        ...mapState('probe', ['activeProbe', 'probeSensorMeasurements', 'selectedSensorMeasurements', 'deleteMeasurements'])
    },
    methods: {
        ...mapActions('probe', ['fetchProbe', 'fetchProbeSensorMeasurements', 'clearSensorMeasurements']),
        ...mapMutations('probe', ['modifySelectedSensorMeasurements', 'setSelectedSensorMeasurements', 'setDeleteMeasurements']),
        handleFetchClick() {
            this.fetchProbeSensorMeasurements({sensorId: this.selectedSensorId, date: this.date})
        },
        handleMeasurementClick(id, type) {
            this.modifySelectedSensorMeasurements({id, type})
        },
        handleClearClick() {
            if (confirm('Are you sure?')) {
                this.clearSensorMeasurements({sensorId: this.selectedSensorId, date: this.date})
            }
        },
        handleHeaderClick(type) {
            let action = 'push';
            let existingOnes = Object.keys(this.selectedSensorMeasurements).filter(ss => this.selectedSensorMeasurements[ss].includes(type))

            if (existingOnes.length === this.probeSensorMeasurements.length) {
                action = 'delete';
            }

            this.probeSensorMeasurements.forEach(pm => {
                if (!Object.keys(this.selectedSensorMeasurements).includes(String(pm.id))) {
                    this.setSelectedSensorMeasurements({index: pm.id, value: []})
                }

                if (action === 'push') {
                    if (!this.selectedSensorMeasurements[pm.id].includes(type)) {
                        let arr = [...this.selectedSensorMeasurements[pm.id]]
                        arr.push(type)
                        this.setSelectedSensorMeasurements({index: pm.id, value: arr})
                    }
                } else {
                    if (this.selectedSensorMeasurements[pm.id].includes(type)) {
                        let arr = Object.values(this.selectedSensorMeasurements[pm.id]).filter(v => v !== type)
                        this.setSelectedSensorMeasurements({index: pm.id, value: arr})
                    }
                }
            })
        },

        handleDeleteClick(measurementId) {
            this.setDeleteMeasurements(measurementId)
        }
    }
}
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
</style>